import React, { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const API_BASE = process.env.REACT_APP_API_URL;

// Point-eligible question types per subject, used by the offline-points award
// form. maxMarks must match the server's POINTS_CONFIG for each archetype.
const OFFLINE_AWARD_SUBJECTS = [
  {
    id: 'geography', label: 'Geography',
    archetypes: [
      { id: 'outline_4',  label: 'Outline',        maxMarks: 4 },
      { id: 'data_6',     label: 'Data response',  maxMarks: 6 },
      { id: 'assess_6',   label: 'Assess',         maxMarks: 6 },
      { id: 'scaffold_9', label: '9-mark essay',   maxMarks: 9 },
      { id: 'essay_20',   label: '20-mark essay',  maxMarks: 20 },
    ],
  },
  {
    id: 'env-science', label: 'Environmental Science',
    archetypes: [
      { id: 'envsci_short', label: 'Short answer',   maxMarks: 6 },
      { id: 'envsci_9',     label: '9-mark',         maxMarks: 9 },
      { id: 'envsci_25',    label: '25-mark essay',  maxMarks: 25 },
    ],
  },
  {
    id: 'gcse-science', label: 'GCSE Science',
    archetypes: [
      { id: 'gcse_science_answer',     label: 'Short answer (≤6)', maxMarks: 6 },
      { id: 'gcse_science_extended_6', label: '6-mark extended',   maxMarks: 6 },
    ],
  },
];

// ─── Formatters ───────────────────────────────────────────────────────────────

function fmtMinutes(seconds) {
  const n = Number(seconds ?? 0);
  const m = Math.floor(n / 60);
  const s = n % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function fmtDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function shortId(auth0Id) {
  const parts = auth0Id.split('|');
  const id = parts[parts.length - 1];
  return id.length > 12 ? '…' + id.slice(-12) : id;
}

function displayEmail(email, auth0Id) {
  return email || shortId(auth0Id);
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = {
    active:  { dot: 'bg-[#3fb950] animate-pulse', text: 'text-[#3fb950]', bg: 'bg-[#3fb950]/10 border-[#3fb950]/30', label: 'Active' },
    paused:  { dot: 'bg-[#d29922]',               text: 'text-[#d29922]', bg: 'bg-[#d29922]/10 border-[#d29922]/30', label: 'Paused' },
    offline: { dot: 'bg-[#8b949e]',               text: 'text-[#8b949e]', bg: 'bg-[#8b949e]/10 border-[#8b949e]/30', label: 'Offline' },
  }[status] ?? { dot: 'bg-[#8b949e]', text: 'text-[#8b949e]', bg: 'bg-[#8b949e]/10 border-[#8b949e]/30', label: 'Offline' };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-mono ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ─── Focus Score Bar ──────────────────────────────────────────────────────────

function FocusBar({ score }) {
  const pct = Math.min(100, Math.max(0, Number(score ?? 0)));
  const colour = pct < 40 ? '#58a6ff' : pct < 70 ? '#d29922' : '#3fb950';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-[#21262d] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: colour }} />
      </div>
      <span className="text-[10px] font-mono tabular-nums" style={{ color: colour }}>{pct}%</span>
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, colour = 'text-[#58a6ff]' }) {
  return (
    <div className="bg-[#161b22] border border-[#30363d] rounded-lg px-4 py-4">
      <p className="text-[#8b949e] text-[10px] uppercase tracking-wider font-mono mb-1">{label}</p>
      <p className={`text-2xl font-bold font-mono ${colour}`}>{value}</p>
    </div>
  );
}

// ─── Archetype Badge ──────────────────────────────────────────────────────────

function ArchetypeBadge({ archetype }) {
  const colours = {
    outline_4: 'border-slate-500/40 text-slate-400',
    assess_6:  'border-amber-500/40 text-amber-400',
    data_6:    'border-cyan-500/40 text-cyan-400',
    assess_9:  'border-purple-500/40 text-purple-400',
    essay_20:  'border-green-500/40 text-green-400',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded border text-[9px] font-mono uppercase ${colours[archetype] || 'border-[#30363d] text-[#8b949e]'}`}>
      {archetype?.replace('_', ' ')}
    </span>
  );
}

// ─── Chart Tooltip ────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#161b22] border border-[#30363d] rounded px-3 py-2 text-xs">
      <p className="text-[#8b949e] mb-1">{fmtDate(label)}</p>
      <p className="text-[#58a6ff]">{fmtMinutes(payload[0]?.value ?? 0)} active</p>
      {payload[1] && <p className="text-[#3fb950]">{payload[1].value} user{payload[1].value !== 1 ? 's' : ''}</p>}
    </div>
  );
}

// ─── Dashboard Shell ──────────────────────────────────────────────────────────

export default function UsageDashboard({ onClose }) {
  const { getAccessTokenSilently } = useAuth0();

  const [tab, setTab]             = useState('students'); // 'students' | 'interactions' | 'quiz'
  const [view, setView]           = useState('list');     // 'list' | 'profile'
  const [selectedUser, setSelectedUser] = useState(null);

  const [students, setStudents]   = useState([]);
  const [daily, setDaily]         = useState([]);
  const [profile, setProfile]     = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [intTotal, setIntTotal]   = useState(0);
  const [intPage, setIntPage]     = useState(1);

  const [quizData, setQuizData]       = useState([]);
  const [quizTotal, setQuizTotal]     = useState(0);
  const [quizPage, setQuizPage]       = useState(1);
  const [quizLoading, setQuizLoading] = useState(false);

  const [awardOpen, setAwardOpen] = useState(false);

  const [loading, setLoading]     = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [intLoading, setIntLoading] = useState(false);
  const [error, setError]         = useState(null);

  // Payload panel — fetched on demand when user clicks a request/response link
  const [payloadInfo, setPayloadInfo] = useState(null); // { attemptId, type, prevView }
  const [payloadData, setPayloadData] = useState({ data: null, loading: false, error: null });

  // Close on Escape
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { Authorization: `Bearer ${token}` };
  }, [getAccessTokenSilently]);

  // ── Load students + daily chart ────────────────────────────────────────────
  const loadStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const [sRes, dRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/students`, { headers }),
        fetch(`${API_BASE}/api/admin/daily-activity`, { headers }),
      ]);
      if (!sRes.ok || !dRes.ok) throw new Error('Failed to fetch dashboard data');
      const [s, d] = await Promise.all([sRes.json(), dRes.json()]);
      setStudents(s);
      setDaily(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  // ── Load student profile ───────────────────────────────────────────────────
  const loadProfile = useCallback(async (userId) => {
    setProfileLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/admin/students/${userId}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch student profile');
      const data = await res.json();
      setProfile(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setProfileLoading(false);
    }
  }, [authHeaders]);

  const openProfile = (student) => {
    setSelectedUser(student);
    setView('profile');
    loadProfile(student.userId);
  };

  // ── Load interactions ──────────────────────────────────────────────────────
  const loadInteractions = useCallback(async (page = 1) => {
    setIntLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/admin/interactions?page=${page}&limit=50`, { headers });
      if (!res.ok) throw new Error('Failed to fetch interactions');
      const data = await res.json();
      setInteractions(data.interactions);
      setIntTotal(data.total);
      setIntPage(page);
    } catch (err) {
      setError(err.message);
    } finally {
      setIntLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (tab === 'interactions' && interactions.length === 0) {
      loadInteractions(1);
    }
  }, [tab, interactions.length, loadInteractions]);

  // ── Load quiz results ──────────────────────────────────────────────────────
  const loadQuizResults = useCallback(async (page = 1) => {
    setQuizLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/admin/quiz-results?page=${page}&limit=50`, { headers });
      if (!res.ok) throw new Error('Failed to fetch quiz results');
      const data = await res.json();
      setQuizData(data.results);
      setQuizTotal(data.total);
      setQuizPage(page);
    } catch (err) {
      setError(err.message);
    } finally {
      setQuizLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (tab === 'quiz' && quizData.length === 0) {
      loadQuizResults(1);
    }
  }, [tab, quizData.length, loadQuizResults]);

  // ── Open payload panel ─────────────────────────────────────────────────────
  const openPayload = useCallback((attemptId, type) => {
    setPayloadInfo({ attemptId, type, prevView: view });
    setPayloadData({ data: null, loading: true, error: null });
    setView('payload');
  }, [view]);

  // Fetch payload when payloadInfo is set
  useEffect(() => {
    if (!payloadInfo) return;
    let cancelled = false;
    authHeaders().then((headers) =>
      fetch(`${API_BASE}/api/admin/attempt-logs/${payloadInfo.attemptId}`, { headers })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data) => { if (!cancelled) setPayloadData({ data, loading: false, error: null }); })
        .catch((err) => { if (!cancelled) setPayloadData({ data: null, loading: false, error: err.message }); })
    );
    return () => { cancelled = true; };
  }, [payloadInfo, authHeaders]);

  // ── Aggregate stats ────────────────────────────────────────────────────────
  const totalActiveSeconds = students.reduce((s, u) => s + Number(u.totalSeconds ?? 0), 0);
  const totalQuestions     = students.reduce((s, u) => s + Number(u.questionCount ?? 0), 0);

  // ── Header ─────────────────────────────────────────────────────────────────
  const Header = () => (
    <div className="flex items-center justify-between px-6 py-4 border-b border-[#30363d] flex-shrink-0">
      <div className="flex items-center gap-3">
        {view === 'profile' && (
          <button
            onClick={() => { setView('list'); setProfile(null); }}
            className="flex items-center gap-1 text-[#8b949e] hover:text-[#e6edf3] transition-colors text-xs font-mono mr-1"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Students
          </button>
        )}
        {view === 'payload' && (
          <button
            onClick={() => {
              const prev = payloadInfo?.prevView ?? 'list';
              setView(prev);
              setPayloadInfo(null);
              setPayloadData({ data: null, loading: false, error: null });
            }}
            className="flex items-center gap-1 text-[#8b949e] hover:text-[#e6edf3] transition-colors text-xs font-mono mr-1"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        )}
        <svg className="w-4 h-4 text-[#58a6ff]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h2 className="text-[#e6edf3] text-sm font-semibold">
          {view === 'profile'
            ? displayEmail(selectedUser?.email, selectedUser?.auth0Id)
            : view === 'payload'
              ? `${payloadInfo?.type === 'request' ? 'Request' : 'Response'} Payload`
              : 'Admin Intelligence Suite'}
        </h2>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-[#30363d] text-[#8b949e]">ADMIN</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={view === 'profile' ? () => loadProfile(selectedUser.userId) : loadStudents}
          disabled={loading || profileLoading || view === 'payload'}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff]/40 transition-colors text-xs font-mono disabled:opacity-40"
        >
          <svg className={`w-3 h-3 ${(loading || profileLoading) ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] transition-colors text-xs font-mono"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Close
        </button>
      </div>
    </div>
  );

  // ── Tab bar (only in list view) ────────────────────────────────────────────
  const TabBar = () => (
    <div className="flex border-b border-[#30363d] px-6 flex-shrink-0">
      {[
        { id: 'students',     label: 'Students' },
        { id: 'interactions', label: 'Interaction Ledger' },
        { id: 'quiz',         label: 'Quiz Results' },
      ].map((t) => (
        <button
          key={t.id}
          onClick={() => setTab(t.id)}
          className={`px-4 py-2.5 text-xs font-mono border-b-2 transition-colors ${
            tab === t.id
              ? 'border-[#58a6ff] text-[#58a6ff]'
              : 'border-transparent text-[#8b949e] hover:text-[#e6edf3]'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  if (loading && students.length === 0) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0d1117] flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0d1117] flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-[#f85149] text-sm mb-2">{error}</p>
            <button onClick={loadStudents} className="text-xs text-[#58a6ff] hover:underline">Retry</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Profile View ───────────────────────────────────────────────────────────
  if (view === 'profile') {
    const p = profile?.profile;
    const feed = profile?.interactions ?? [];

    return (
      <div className="fixed inset-0 z-50 bg-[#0d1117] flex flex-col">
        <Header />
        {profileLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
          </div>
        ) : p ? (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Profile header */}
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#e6edf3] font-semibold text-sm">{displayEmail(p.email, p.auth0Id)}</p>
                <p className="text-[#8b949e] text-xs mt-0.5 font-mono">
                  Member since {fmtDate(p.firstSeen)} · Last active {fmtDateTime(p.lastSeen)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setAwardOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#d29922]/40 text-[#d29922] hover:bg-[#d29922]/10 transition-colors text-xs font-mono"
                >
                  <span className="text-sm leading-none">⭐</span>
                  Award offline points
                </button>
                <StatusBadge status={selectedUser?.status} />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <StatCard label="Active Time"   value={fmtMinutes(p.totalSeconds)}  colour="text-[#3fb950]" />
              <StatCard label="Questions"     value={p.questionCount ?? 0}         colour="text-[#d2a8ff]" />
              <StatCard label="Avg Latency"   value={p.avgLatencyMs ? `${p.avgLatencyMs}ms` : '—'} colour="text-[#d29922]" />
              <div className="bg-[#161b22] border border-[#30363d] rounded-lg px-4 py-4">
                <p className="text-[#8b949e] text-[10px] uppercase tracking-wider font-mono mb-2">Focus Score</p>
                <FocusBar score={p.focusScore} />
                <p className="text-[#8b949e] text-[9px] mt-1.5">Active typing vs total session time</p>
              </div>
            </div>

            {/* Interaction Feed */}
            <div>
              <p className="text-[#8b949e] text-[10px] uppercase tracking-wider font-mono mb-3">
                Interaction Feed ({feed.length})
              </p>
              {feed.length === 0 ? (
                <p className="text-[#484f58] text-xs italic">No interactions yet.</p>
              ) : (
                <div className="space-y-3">
                  {feed.map((item) => (
                    <InteractionCard key={item.attemptId} item={item} onViewPayload={openPayload} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}

        {awardOpen && (
          <AwardPointsModal
            userId={selectedUser.userId}
            displayName={displayEmail(selectedUser?.email, selectedUser?.auth0Id)}
            authHeaders={authHeaders}
            onClose={() => setAwardOpen(false)}
          />
        )}
      </div>
    );
  }

  // ── Payload View ───────────────────────────────────────────────────────────
  if (view === 'payload') {
    return (
      <div className="fixed inset-0 z-50 bg-[#0d1117] flex flex-col">
        <Header />
        <PayloadPanel info={payloadInfo} payloadData={payloadData} />
      </div>
    );
  }

  // ── List View ──────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 bg-[#0d1117] flex flex-col">
      <Header />
      <TabBar />

      {tab === 'students' && (
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Total Students"    value={students.length}            colour="text-[#58a6ff]" />
            <StatCard label="Total Active Time" value={fmtMinutes(totalActiveSeconds)} colour="text-[#3fb950]" />
            <StatCard label="Total Questions"   value={totalQuestions}             colour="text-[#d2a8ff]" />
          </div>

          {/* Daily chart */}
          {daily.length > 0 && (
            <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4">
              <p className="text-[#8b949e] text-[10px] uppercase tracking-wider font-mono mb-4">
                Daily Activity — Last 30 Days
              </p>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={daily} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#21262d" vertical={false} />
                  <XAxis dataKey="day" tickFormatter={fmtDate} tick={{ fill: '#8b949e', fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tickFormatter={(v) => `${Math.floor(v / 60)}m`} tick={{ fill: '#8b949e', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: '#ffffff08' }} />
                  <Bar dataKey="totalSeconds" fill="#58a6ff" radius={[3, 3, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Student table */}
          <div className="bg-[#161b22] border border-[#30363d] rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#8b949e] font-mono text-[10px] uppercase border-b border-[#30363d]">
                  <th className="text-left px-4 py-2.5 font-normal">Status</th>
                  <th className="text-left px-4 py-2.5 font-normal">Student</th>
                  <th className="text-right px-4 py-2.5 font-normal">Active Time</th>
                  <th className="text-right px-4 py-2.5 font-normal">Focus</th>
                  <th className="text-right px-4 py-2.5 font-normal">Questions</th>
                  <th className="text-right px-4 py-2.5 font-normal">Last Active</th>
                </tr>
              </thead>
              <tbody>
                {students.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-[#484f58] italic">No students yet.</td></tr>
                ) : students.map((s, i) => (
                  <StudentRow key={s.userId} student={s} index={i} onClick={() => openProfile(s)} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'interactions' && (
        <InteractionsTab
          interactions={interactions}
          total={intTotal}
          page={intPage}
          loading={intLoading}
          onPageChange={(p) => loadInteractions(p)}
          onViewPayload={openPayload}
        />
      )}

      {tab === 'quiz' && (
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[#8b949e] text-[10px] uppercase tracking-wider font-mono">
              All MCQ Quiz Attempts ({quizTotal})
            </p>
            <button
              onClick={() => loadQuizResults(1)}
              disabled={quizLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] transition-colors text-xs font-mono disabled:opacity-40"
            >
              <svg className={`w-3 h-3 ${quizLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>

          {quizLoading && quizData.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-5 h-5 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
            </div>
          ) : (
            <div className="bg-[#161b22] border border-[#30363d] rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[#8b949e] font-mono text-[10px] uppercase border-b border-[#30363d]">
                    <th className="text-left px-4 py-2.5 font-normal">Student</th>
                    <th className="text-left px-4 py-2.5 font-normal">Topic</th>
                    <th className="text-right px-4 py-2.5 font-normal">Score</th>
                    <th className="text-right px-4 py-2.5 font-normal">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {quizData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-[#484f58] italic">
                        No quiz attempts yet.
                      </td>
                    </tr>
                  ) : quizData.map((q, i) => {
                    const pct = Math.round((q.score / q.totalQuestions) * 100);
                    const colour = pct >= 80 ? '#3fb950' : pct >= 60 ? '#d29922' : '#f85149';
                    const topicLabel = {
                      water_carbon: 'Water & Carbon', hazards: 'Hazards',
                      deserts: 'Hot Deserts', changing_places: 'Changing Places',
                      global_systems: 'Global Systems', resource_security: 'Resource Security',
                      random_mix: 'Random Mix',
                    }[q.topic] ?? q.topic;

                    return (
                      <tr key={q.id} className={`border-b border-[#21262d] last:border-0 ${i % 2 === 0 ? '' : 'bg-[#0d1117]/30'}`}>
                        <td className="px-4 py-3 text-[#e6edf3] font-mono">{displayEmail(q.email, q.auth0Id)}</td>
                        <td className="px-4 py-3 text-[#8b949e]">{topicLabel}</td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold" style={{ color: colour }}>
                          {q.score}/{q.totalQuestions}
                          <span className="text-[#484f58] font-normal ml-1">({pct}%)</span>
                        </td>
                        <td className="px-4 py-3 text-right text-[#484f58] font-mono tabular-nums">{fmtDateTime(q.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {quizTotal > 50 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-[#484f58] text-[10px] font-mono">
                Page {quizPage} · {quizTotal} total
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => loadQuizResults(quizPage - 1)}
                  disabled={quizPage <= 1 || quizLoading}
                  className="px-3 py-1.5 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] text-xs font-mono disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  onClick={() => loadQuizResults(quizPage + 1)}
                  disabled={quizPage * 50 >= quizTotal || quizLoading}
                  className="px-3 py-1.5 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] text-xs font-mono disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Student Row ──────────────────────────────────────────────────────────────

function StudentRow({ student: s, index, onClick }) {
  const elapsed = s.lastSeen && s.firstSeen
    ? (new Date(s.lastSeen) - new Date(s.firstSeen)) / 1000
    : 0;
  const focusPct = elapsed > 0 ? Math.min(100, Math.round(Number(s.totalSeconds) / elapsed * 100)) : 0;

  return (
    <tr
      onClick={onClick}
      className={`border-b border-[#21262d] last:border-0 cursor-pointer hover:bg-[#161b22] transition-colors ${index % 2 === 0 ? '' : 'bg-[#0d1117]/30'}`}
    >
      <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
      <td className="px-4 py-3 text-[#e6edf3] font-mono">{displayEmail(s.email, s.auth0Id)}</td>
      <td className="px-4 py-3 text-right text-[#3fb950] font-mono tabular-nums">{fmtMinutes(s.totalSeconds)}</td>
      <td className="px-4 py-3 text-right w-32"><FocusBar score={focusPct} /></td>
      <td className="px-4 py-3 text-right text-[#d2a8ff] font-mono tabular-nums">{s.questionCount ?? 0}</td>
      <td className="px-4 py-3 text-right text-[#8b949e] font-mono tabular-nums">{fmtDate(s.lastSeen)}</td>
    </tr>
  );
}

// ─── Interaction Card helpers ─────────────────────────────────────────────────

const ARCHETYPE_MAX = { outline_4: 4, assess_6: 6, data_6: 6, assess_9: 9, essay_20: 20 };

function parseJson(str) {
  if (!str) return null;
  try { return typeof str === 'object' ? str : JSON.parse(str); }
  catch { return null; }
}

function extractQuestionText(promptPayload, archetype) {
  try {
    if (archetype === 'outline_4' || archetype === 'assess_6') {
      const user = parseJson(promptPayload?.user);
      return user?.question_text ?? null;
    }
    // Gemini format: "QUESTION (N marks): [text]\n..."
    const match = String(promptPayload?.user ?? '').match(/^QUESTION \(\d+ marks\):\s*(.+?)(?:\n|CASE STUDY:)/s);
    return match?.[1]?.trim() ?? null;
  } catch { return null; }
}

function getScoreDisplay(parsed, archetype) {
  if (!parsed) return null;
  const max = ARCHETYPE_MAX[archetype];
  if (parsed.total !== undefined && max) return { score: parsed.total, max, label: `${parsed.total}/${max}` };
  if (parsed.score !== undefined) return { score: parsed.score, max, label: parsed.scoreLabel || `${parsed.score}${max ? `/${max}` : ''}` };
  return null;
}

function scoreColour(score, max) {
  if (!max) return 'text-[#58a6ff]';
  const pct = score / max;
  if (pct >= 0.75) return 'text-[#3fb950]';
  if (pct >= 0.5)  return 'text-[#d29922]';
  return 'text-[#f85149]';
}

function getExaminerNote(parsed) {
  return parsed?.examiner_note ?? parsed?.examinerNote ?? null;
}

function formatStudentAnswer(studentAnswer) {
  if (!studentAnswer || typeof studentAnswer !== 'object') return null;
  const entries = Object.entries(studentAnswer).filter(([, v]) => v && String(v).trim());
  if (!entries.length) return null;
  return entries.map(([, v]) => String(v).trim()).join(' ');
}

// ─── Payload Panel (rendered in-column, fetched on demand) ───────────────────

function PayloadPanel({ info, payloadData }) {
  const { data, loading, error } = payloadData;
  const payload = info?.type === 'request' ? data?.promptPayload : data?.responsePayload;

  return (
    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-4">
      {/* Meta strip */}
      {data && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] font-mono text-[#8b949e]">
          {data.provider && <span>{data.provider}</span>}
          {data.model    && <span className="text-[#484f58]">{data.model}</span>}
          {data.inputTokens  != null && <span>{data.inputTokens} in</span>}
          {data.outputTokens != null && <span className="text-[#3fb950]">{data.outputTokens} out</span>}
          {data.latencyMs    != null && <span className="text-[#d29922]">{data.latencyMs}ms</span>}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-5 h-5 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <p className="text-[#f85149] text-xs font-mono py-8 text-center">{error}</p>
      )}

      {!loading && !error && payload != null && (
        <pre className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 text-[10px] text-[#8b949e] font-mono whitespace-pre-wrap break-words overflow-auto">
          {JSON.stringify(payload, null, 2)}
        </pre>
      )}

      {!loading && !error && data && payload == null && (
        <p className="text-[#484f58] text-xs font-mono py-8 text-center">No payload recorded for this attempt.</p>
      )}
    </div>
  );
}

// ─── Interaction Card (profile feed) ─────────────────────────────────────────

const POINT_REASON_LABELS = {
  quality:         'Quality',
  correct:         'Correct answer',
  milestone_bonus: 'Milestone bonus',
  offline_award:   'Offline award',
  streak_bonus:    'Streak bonus',
  time_engagement: 'Time engagement',
};

function pointReasonLabel(reason) {
  return POINT_REASON_LABELS[reason]
    ?? reason.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function InteractionCard({ item, onViewPayload }) {
  const [showDetails, setShowDetails] = useState(false);

  const parsed        = parseJson(item.aiResponseText);
  const questionText  = extractQuestionText(item.promptPayload, item.archetype);
  const scoreInfo     = getScoreDisplay(parsed, item.archetype);
  const examinerNote  = getExaminerNote(parsed);
  const studentAnswer = formatStudentAnswer(item.studentAnswer);
  const success       = !item.errorMessage;
  const cardFeedback = parsed?.card_feedback;
  const pointsBreakdown = item.pointsBreakdown ?? [];
  const pointsAwarded   = item.pointsAwarded ?? 0;
  const hasDetails   = !!cardFeedback || (parsed?.score !== undefined) || pointsBreakdown.length > 0;

  return (
    <div className="bg-[#161b22] border border-[#30363d] rounded-lg overflow-hidden">

        {/* Header row */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#21262d]">
          <ArchetypeBadge archetype={item.archetype} />
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-[#30363d] text-[#8b949e]">
            {item.provider}
          </span>
          {scoreInfo && (
            <span className={`text-sm font-bold font-mono ${scoreColour(scoreInfo.score, scoreInfo.max)}`}>
              {scoreInfo.label}
            </span>
          )}
          <button
            type="button"
            onClick={() => hasDetails && setShowDetails((p) => !p)}
            title={hasDetails ? 'Show score explanation' : 'No explanation available'}
            className={`text-xs font-bold font-mono px-1.5 py-0.5 rounded border transition-colors ${
              pointsAwarded > 0
                ? 'border-[#d29922]/40 text-[#d29922]'
                : 'border-[#30363d] text-[#484f58]'
            } ${hasDetails ? 'hover:bg-[#d29922]/10 cursor-pointer' : 'cursor-default'}`}
          >
            {pointsAwarded > 0 ? `+${pointsAwarded}` : '0'} pts
          </button>
          <span className="text-[#8b949e] text-[10px] font-mono ml-auto">{item.latencyMs}ms</span>
          <span className="text-[#8b949e] text-[10px] font-mono">{fmtDateTime(item.createdAt)}</span>
        </div>

        <div className="px-4 py-3 space-y-3">

          {/* Question text */}
          {questionText ? (
            <p className="text-[#e6edf3] text-xs leading-relaxed">{questionText}</p>
          ) : (
            <p className="text-[#484f58] text-[10px] font-mono">{item.questionId}</p>
          )}

          {/* Student answer */}
          {studentAnswer && (
            <div className="bg-[#0d1117] border border-[#21262d] rounded px-3 py-2">
              <p className="text-[9px] font-mono uppercase tracking-wider text-[#484f58] mb-1.5">Student answer</p>
              <p className="text-[11px] text-[#8b949e] leading-relaxed">
                {studentAnswer}
              </p>
            </div>
          )}

          {/* Examiner note / overall comment */}
          {examinerNote && (
            <p className="text-[#8b949e] text-xs italic border-l-2 border-[#30363d] pl-3">
              {examinerNote}
            </p>
          )}

          {/* Error */}
          {item.errorMessage && (
            <p className="text-[#f85149] text-[10px] font-mono">{item.errorMessage}</p>
          )}

          {/* Action row */}
          <div className="flex items-center gap-3 pt-1">
            {hasDetails && (
              <button
                onClick={() => setShowDetails((p) => !p)}
                className="text-[10px] text-[#58a6ff] hover:underline font-mono"
              >
                {showDetails ? '▲ Hide details' : '▼ Show details'}
              </button>
            )}
            <button
              onClick={() => onViewPayload(item.attemptId, 'request')}
              className="text-[10px] text-[#8b949e] hover:text-[#58a6ff] font-mono transition-colors underline-offset-2 hover:underline"
            >
              View request
            </button>
            {success && (
              <button
                onClick={() => onViewPayload(item.attemptId, 'response')}
                className="text-[10px] text-[#8b949e] hover:text-[#58a6ff] font-mono transition-colors underline-offset-2 hover:underline"
              >
                View response
              </button>
            )}
          </div>

          {/* Details: points breakdown */}
          {showDetails && pointsBreakdown.length > 0 && (
            <div className="bg-[#0d1117] border border-[#21262d] rounded p-3 space-y-1.5">
              <p className="text-[9px] font-mono uppercase tracking-wider text-[#484f58]">Points awarded</p>
              {pointsBreakdown.map((b, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-[10px] text-[#8b949e]">{pointReasonLabel(b.reason)}</span>
                  <span className="text-[10px] font-bold font-mono text-[#d29922]">+{b.points}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-[#21262d] pt-1.5">
                <span className="text-[10px] font-semibold text-[#e6edf3]">Total</span>
                <span className="text-xs font-bold font-mono text-[#d29922]">+{pointsAwarded}</span>
              </div>
            </div>
          )}

          {/* Details: card-by-card breakdown */}
          {showDetails && cardFeedback && (
            <div className="space-y-2 pt-1">
              {Object.entries(cardFeedback).map(([cardKey, cardData]) => {
                const studentText = item.studentAnswer?.[cardKey];
                const marks    = cardData.marks;
                const maxMarks = item.archetype === 'outline_4' ? 1
                  : cardKey === 'OVERALL JUDGMENT' ? null
                  : cardKey.includes('AO1') || cardKey === 'CONTEXT (AO1)' ? 2 : 2;

                return (
                  <div key={cardKey} className="bg-[#0d1117] border border-[#21262d] rounded p-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono font-semibold text-[#e6edf3]">{cardKey}</span>
                      {marks !== undefined && maxMarks !== null && (
                        <span className={`text-xs font-bold font-mono ${scoreColour(marks, maxMarks)}`}>
                          {marks}/{maxMarks}
                        </span>
                      )}
                    </div>
                    {studentText && (
                      <p className="text-[10px] text-[#8b949e] leading-relaxed border-l border-[#30363d] pl-2">
                        {String(studentText)}
                      </p>
                    )}
                    {cardData.comment && (
                      <p className="text-[10px] text-[#e6edf3]">
                        <span className="text-[#484f58]">Comment: </span>{cardData.comment}
                      </p>
                    )}
                    {cardData.minimal_improvement && (
                      <p className="text-[10px] text-[#d29922]">
                        <span className="text-[#484f58]">Improve: </span>{cardData.minimal_improvement}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Details: Gemini format */}
          {showDetails && !cardFeedback && parsed?.score !== undefined && (
            <div className="space-y-2 pt-1">
              {parsed.aoBreakdown && Object.entries(parsed.aoBreakdown).map(([ao, comment]) => (
                <div key={ao} className="bg-[#0d1117] border border-[#21262d] rounded p-3">
                  <p className="text-[10px] font-mono font-semibold text-[#e6edf3] mb-1">{ao}</p>
                  <p className="text-[10px] text-[#8b949e]">{comment}</p>
                </div>
              ))}
              {parsed.strengths?.length > 0 && (
                <div className="bg-[#0d1117] border border-[#21262d] rounded p-3">
                  <p className="text-[10px] font-mono font-semibold text-[#3fb950] mb-1.5">Strengths</p>
                  <ul className="space-y-0.5">
                    {parsed.strengths.map((s, i) => (
                      <li key={i} className="text-[10px] text-[#8b949e] flex gap-1.5"><span className="text-[#3fb950]">+</span>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {parsed.nextSteps?.length > 0 && (
                <div className="bg-[#0d1117] border border-[#21262d] rounded p-3">
                  <p className="text-[10px] font-mono font-semibold text-[#d29922] mb-1.5">Next Steps</p>
                  <ul className="space-y-0.5">
                    {parsed.nextSteps.map((s, i) => (
                      <li key={i} className="text-[10px] text-[#8b949e] flex gap-1.5"><span className="text-[#d29922]">→</span>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
  );
}

// ─── Interactions Tab (global ledger) ────────────────────────────────────────

function InteractionsTab({ interactions, total, page, loading, onPageChange, onViewPayload }) {
  const totalPages = Math.ceil(total / 50);

  return (
    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[#8b949e] text-[10px] font-mono uppercase tracking-wider">
          {total} total interactions
        </p>
        {totalPages > 1 && (
          <div className="flex items-center gap-2 text-xs font-mono">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1 || loading}
              className="px-2 py-1 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] disabled:opacity-40 transition-colors"
            >
              ←
            </button>
            <span className="text-[#8b949e]">Page {page} of {totalPages}</span>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages || loading}
              className="px-2 py-1 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] disabled:opacity-40 transition-colors"
            >
              →
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-5 h-5 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-[#161b22] border border-[#30363d] rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[#8b949e] font-mono text-[10px] uppercase border-b border-[#30363d]">
                <th className="text-left px-4 py-2.5 font-normal">Student</th>
                <th className="text-left px-4 py-2.5 font-normal">Archetype</th>
                <th className="text-left px-4 py-2.5 font-normal">Provider</th>
                <th className="text-left px-4 py-2.5 font-normal">Result</th>
                <th className="text-right px-4 py-2.5 font-normal">Latency</th>
                <th className="text-right px-4 py-2.5 font-normal">Date</th>
                <th className="text-right px-4 py-2.5 font-normal">Payload</th>
              </tr>
            </thead>
            <tbody>
              {interactions.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-[#484f58] italic">No interactions yet.</td></tr>
              ) : interactions.map((item, i) => (
                <tr key={item.attemptId} className={`border-b border-[#21262d] last:border-0 ${i % 2 === 0 ? '' : 'bg-[#0d1117]/30'}`}>
                  <td className="px-4 py-2.5 font-mono text-[#8b949e]">{displayEmail(item.email, item.auth0Id)}</td>
                  <td className="px-4 py-2.5"><ArchetypeBadge archetype={item.archetype} /></td>
                  <td className="px-4 py-2.5 font-mono text-[#8b949e]">{item.provider}</td>
                  <td className="px-4 py-2.5">
                    {item.errorMessage
                      ? <span className="text-[#f85149]">✗ {item.errorMessage.slice(0, 40)}{item.errorMessage.length > 40 ? '…' : ''}</span>
                      : <span className="text-[#3fb950]">✓ Success</span>
                    }
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-[#d29922]">{item.latencyMs}ms</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-[#8b949e]">{fmtDateTime(item.createdAt)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onViewPayload(item.attemptId, 'request')}
                        className="text-[9px] font-mono text-[#8b949e] hover:text-[#58a6ff] transition-colors underline-offset-2 hover:underline"
                      >
                        req
                      </button>
                      {!item.errorMessage && (
                        <button
                          onClick={() => onViewPayload(item.attemptId, 'response')}
                          className="text-[9px] font-mono text-[#8b949e] hover:text-[#58a6ff] transition-colors underline-offset-2 hover:underline"
                        >
                          rsp
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Award Offline Points Modal ──────────────────────────────────────────────

function offlineSubjectLabel(subjectId) {
  return OFFLINE_AWARD_SUBJECTS.find((s) => s.id === subjectId)?.label ?? subjectId ?? '—';
}

function offlineArchetypeLabel(archetypeId) {
  for (const s of OFFLINE_AWARD_SUBJECTS) {
    const a = s.archetypes.find((x) => x.id === archetypeId);
    if (a) return a.label;
  }
  return archetypeId;
}

function AwardPointsModal({ userId, displayName, authHeaders, onClose }) {
  const [subjectId, setSubjectId]     = useState(OFFLINE_AWARD_SUBJECTS[0].id);
  const subject = OFFLINE_AWARD_SUBJECTS.find((s) => s.id === subjectId) ?? OFFLINE_AWARD_SUBJECTS[0];
  const [archetypeId, setArchetypeId] = useState(subject.archetypes[0].id);
  const archetype = subject.archetypes.find((a) => a.id === archetypeId) ?? subject.archetypes[0];
  const [marks, setMarks]   = useState('');
  const todayStr = new Date().toISOString().split('T')[0];
  const [date, setDate]     = useState(todayStr);
  const [status, setStatus] = useState({ loading: false, error: null, result: null });

  const [awards, setAwards]   = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError]     = useState(null);
  const [deletingId, setDeletingId]   = useState(null);

  const loadAwards = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/admin/students/${userId}/offline-points`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setAwards(data.awards || []);
    } catch (err) {
      setListError(err.message);
    } finally {
      setListLoading(false);
    }
  }, [authHeaders, userId]);

  useEffect(() => { loadAwards(); }, [loadAwards]);

  const deleteAward = async (id) => {
    if (deletingId) return;
    if (!window.confirm('Remove this offline award? The points will be deducted from the student.')) return;
    setDeletingId(id);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/admin/students/${userId}/offline-points/${id}`, {
        method: 'DELETE',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setAwards((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setListError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const resetFeedback = () => setStatus({ loading: false, error: null, result: null });

  const handleSubjectChange = (id) => {
    const next = OFFLINE_AWARD_SUBJECTS.find((s) => s.id === id) ?? OFFLINE_AWARD_SUBJECTS[0];
    setSubjectId(id);
    setArchetypeId(next.archetypes[0].id);
    setMarks('');
    resetFeedback();
  };

  const handleArchetypeChange = (id) => {
    setArchetypeId(id);
    setMarks('');
    resetFeedback();
  };

  const marksNum = Number(marks);
  const marksValid = marks !== '' && Number.isInteger(marksNum) && marksNum >= 0 && marksNum <= archetype.maxMarks;

  const submit = async () => {
    if (!marksValid || status.loading) return;
    setStatus({ loading: true, error: null, result: null });
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/admin/students/${userId}/award-offline-points`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: subjectId, archetype: archetypeId, marks: marksNum, mode: 'guided', awardedAt: date }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setStatus({ loading: false, error: null, result: data });
      setMarks('');
      loadAwards();
    } catch (err) {
      setStatus({ loading: false, error: err.message, result: null });
    }
  };

  const selectClass = 'w-full bg-[#0d1117] border border-[#30363d] rounded px-3 py-2 text-xs font-mono text-[#e6edf3] focus:border-[#58a6ff]/60 focus:outline-none';

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-[#e6edf3] text-sm font-semibold">Award offline points</h3>
          <button onClick={onClose} className="text-[#8b949e] hover:text-[#e6edf3] text-lg leading-none">&times;</button>
        </div>
        <p className="text-[#8b949e] text-xs font-mono mb-5">{displayName}</p>

        <div className="space-y-4">
          {/* Subject */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-mono text-[#8b949e] mb-1.5">Subject</label>
            <select value={subjectId} onChange={(e) => handleSubjectChange(e.target.value)} className={selectClass}>
              {OFFLINE_AWARD_SUBJECTS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Question type */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-mono text-[#8b949e] mb-1.5">Question type</label>
            <select value={archetypeId} onChange={(e) => handleArchetypeChange(e.target.value)} className={selectClass}>
              {subject.archetypes.map((a) => (
                <option key={a.id} value={a.id}>{a.label} ({a.maxMarks} marks)</option>
              ))}
            </select>
          </div>

          {/* Marks */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-mono text-[#8b949e] mb-1.5">
              Marks <span className="text-[#484f58]">/ {archetype.maxMarks}</span>
            </label>
            <input
              type="number"
              min={0}
              max={archetype.maxMarks}
              step={1}
              value={marks}
              onChange={(e) => { setMarks(e.target.value); resetFeedback(); }}
              placeholder={`0 – ${archetype.maxMarks}`}
              className={selectClass}
            />
            {marks !== '' && !marksValid && (
              <p className="text-[#f85149] text-[10px] font-mono mt-1">Enter a whole number between 0 and {archetype.maxMarks}.</p>
            )}
          </div>

          {/* Date the points count against */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-mono text-[#8b949e] mb-1.5">Date awarded</label>
            <input
              type="date"
              max={todayStr}
              value={date}
              onChange={(e) => { setDate(e.target.value); resetFeedback(); }}
              className={selectClass}
            />
          </div>

          {/* Feedback */}
          {status.error && (
            <p className="text-[#f85149] text-xs font-mono">{status.error}</p>
          )}
          {status.result && (
            <p className="text-[#3fb950] text-xs font-mono">
              {status.result.pointsEarned > 0
                ? `Awarded ${status.result.pointsEarned.toLocaleString()} points (${status.result.marks}/${status.result.maxMarks})${status.result.awardedAt ? ` on ${status.result.awardedAt}` : ''}.`
                : `No points — ${status.result.marks}/${status.result.maxMarks} scored 0.`}
            </p>
          )}
        </div>

        {/* Existing manual awards */}
        <div className="mt-6 pt-4 border-t border-[#30363d]">
          <p className="text-[10px] uppercase tracking-wider font-mono text-[#8b949e] mb-2">
            Manual awards this term
          </p>
          {listLoading ? (
            <div className="flex justify-center py-4">
              <div className="w-4 h-4 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
            </div>
          ) : listError ? (
            <p className="text-[#f85149] text-xs font-mono">{listError}</p>
          ) : awards.length === 0 ? (
            <p className="text-[#484f58] text-xs italic">No manual awards yet.</p>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-1.5">
              {awards.map((a) => (
                <div key={a.id} className="flex items-center gap-2 bg-[#0d1117] border border-[#21262d] rounded px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[#e6edf3] text-xs font-mono truncate">
                      {offlineSubjectLabel(a.subject)} · {offlineArchetypeLabel(a.archetype)}
                      {a.marks != null && a.maxMarks != null && (
                        <span className="text-[#484f58]"> ({a.marks}/{a.maxMarks})</span>
                      )}
                    </p>
                    <p className="text-[#484f58] text-[10px] font-mono">{fmtDateTime(a.createdAt)}</p>
                  </div>
                  <span className="text-[#d29922] text-xs font-mono tabular-nums flex-shrink-0">
                    +{a.points.toLocaleString()}
                  </span>
                  <button
                    onClick={() => deleteAward(a.id)}
                    disabled={deletingId === a.id}
                    className="flex-shrink-0 text-[10px] font-mono text-[#8b949e] hover:text-[#f85149] transition-colors disabled:opacity-40"
                  >
                    {deletingId === a.id ? '…' : 'Remove'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] text-xs font-mono transition-colors"
          >
            Close
          </button>
          <button
            onClick={submit}
            disabled={!marksValid || status.loading}
            className="px-3 py-1.5 rounded border border-[#d29922]/40 bg-[#d29922]/10 text-[#d29922] hover:bg-[#d29922]/20 text-xs font-mono transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {status.loading ? 'Awarding…' : 'Award points'}
          </button>
        </div>
      </div>
    </div>
  );
}
